import DOMPurify from 'isomorphic-dompurify'
import MarkdownIT, { type Options as MarkdownITOptions } from 'markdown-it'
import React from 'react'
import styled from 'styled-components'

interface Props {
  markdown: string
  internalLinkHandler?: (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => void
  externalLinks?: boolean
  style?: React.CSSProperties
  options?: MarkdownITOptions
  className?: string
}

export const Markdown: React.FC<Props> = ({
  markdown,
  internalLinkHandler,
  style,
  externalLinks = true,
  options,
  className,
}) => {
  const sanitizedHTML = ((markdown, options) => {
    const renderer = new MarkdownIT('commonmark', options)

    if (externalLinks) {
      DOMPurify.addHook('afterSanitizeAttributes', (node: Element) => {
        // set all elements owning target to target=_blank
        if ('target' in (node as Element)) {
          node.setAttribute('target', '_blank')
          node.setAttribute('rel', 'noopener noreferrer')
        }
      })
    }
    const dirtyHtml = renderer.render(markdown)
    // full list of tags allowed https://github.com/cure53/DOMPurify/blob/main/src/tags.js
    // we remove some extra ones we don't want here
    return DOMPurify.sanitize(dirtyHtml, {
      FORBID_TAGS: [
        'style',
        'img',
        'form',
        'field',
        'input',
        'button',
        'select',
        'textarea',
        'canvas',
        'audio',
        'video',
        'source',
        'picture',
      ],
      USE_PROFILES: { html: true },
    })
  })(markdown, options)

  return (
    <Container className={className}>
      <RenderHtml
        internalLinks={!externalLinks}
        internalLinkHandler={internalLinkHandler}
        sanitizedHTML={sanitizedHTML}
        style={style}
      />
    </Container>
  )
}

interface HtmlProps {
  internalLinks: boolean
  internalLinkHandler?: (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => void
  style?: React.CSSProperties
  sanitizedHTML: string
}

const RenderHtml: React.FC<HtmlProps> = ({ internalLinks, internalLinkHandler, style, sanitizedHTML }) => {
  if (internalLinks)
    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
      <span
        className="markdown"
        onClick={internalLinkHandler}
        style={style}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
      />
    )
  return (
    <span
      className="markdown"
      style={style}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
    />
  )
}

const Container = styled.div`
  white-space: normal;

  > * > * {
    margin: 0;
    padding: 0;
  }

  > * > ul {
    margin-left: 2rem;
    list-style: disc;
    display: flex;
    flex-direction: column;
  }

  > * {
    color: ${({ theme }) => theme.colors.dark};
  }

  strong {
    font-weight: 600;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-block-end: 1.6rem;
  }

  p,
  ul {
    margin-block-end: 1.6rem;
    line-height: 3.2rem;
  }

  * > li {
    line-height: 3.2rem;
    margin-block-end: 0.5rem;
  }

  * > li > * {
    margin-block-end: 0rem;
  }

  * > a {
    color: ${({ theme }) => theme.colors.secondary.medium};
    &:hover {
      text-decoration: underline;
    }
  }
`
